const PHASES = [
  { key: 'machine-check', label: 'Checking browser and computer readiness' },
  { key: 'websocket-start', label: 'Opening notifier connection' },
  { key: 'idle', label: 'Idle latency baseline' },
  { key: 'bandwidth', label: 'Bandwidth and capacity checks' },
  { key: 'bufferbloat', label: 'Latency under load' },
  { key: 'webrtc', label: 'UDP loss, jitter, and RTT' },
  { key: 'capture-env', label: 'Capturing environment' }
];

const PHASE_LABELS = Object.fromEntries(PHASES.map(step => [step.key, step.label]));

function clampPct(value) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
}

function formatTime(seconds) {
  return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
}

function formatMb(bytes) {
  if (!Number.isFinite(bytes)) return '0 MB';
  return `${Math.max(0, bytes / 1024 / 1024).toFixed(1)} MB`;
}

function progressFor({ phase, elapsedSec, totalSec, progress }) {
  if (phase === 'bandwidth' && progress?.probe === 'bandwidth') {
    if (progress.stage === 'single-download' || progress.stage === 'download') {
      const downloadPct = progress.totalBytes
        ? (progress.bytes / progress.totalBytes) * 50
        : 0;
      return {
        pct: clampPct(downloadPct),
        detail: `Sampling single-stream download: ${formatMb(progress.bytes)} received`
      };
    }
    if (progress.stage === 'capacity-download' || progress.stage === 'capacity-upload') {
      const label = progress.stage === 'capacity-download'
        ? 'Multi-stream download capacity'
        : 'Multi-stream upload capacity';
      const pct = progress.durationMs && progress.elapsedMs !== undefined
        ? 60 + (progress.elapsedMs / progress.durationMs) * 35
        : 65;
      return {
        pct: clampPct(pct),
        detail: `${label}: ${formatMb(progress.bytes)} transferred across ${progress.streams || 1} streams`
      };
    }
    const uploadDone = progress.stage === 'completed' || progress.stage === 'timed-out';
    return {
      pct: uploadDone ? 100 : 55,
      detail: uploadDone ? 'Upload step complete' : 'Uploading test payload'
    };
  }

  if (progress?.durationMs && progress?.elapsedMs !== undefined) {
    return {
      pct: clampPct((progress.elapsedMs / progress.durationMs) * 100),
      detail: null
    };
  }

  if (phase === 'webrtc' && progress?.probe === 'webrtc' && progress.expectedPackets) {
    return {
      pct: clampPct((progress.sent / progress.expectedPackets) * 100),
      detail: `Voice-quality packets: ${progress.sent || 0} sent`
    };
  }

  return {
    pct: totalSec ? Math.min(95, clampPct((elapsedSec / totalSec) * 100)) : 5,
    detail: null
  };
}

export default function ProgressScreen({ phase, elapsedSec, totalSec, progress }) {
  const currentStepIndex = Math.max(0, PHASES.findIndex(step => step.key === phase));
  const currentStep = PHASES[currentStepIndex] || { key: phase, label: PHASE_LABELS[phase] || 'Working' };
  const { pct: phasePct, detail } = progressFor({ phase, elapsedSec, totalSec, progress });
  const pct = clampPct(((currentStepIndex + phasePct / 100) / PHASES.length) * 100);
  const overExpected = totalSec && elapsedSec > totalSec;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-900">Test in progress</h1>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Step {currentStepIndex + 1} of {PHASES.length}
        </p>
        <p className="text-sm text-slate-600">
          {currentStep.label}
        </p>
        <p className="mt-3 text-xs font-medium text-slate-500">Overall step progress</p>
        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100">
          <div
            data-testid="progress-bar"
            className="h-full bg-slate-900 transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Elapsed {formatTime(elapsedSec)}
          {overExpected ? ' - taking longer than a typical run. Please keep this tab open so the result can be recorded.' : ''}
        </p>
        {detail && (
          <p className="mt-1 text-xs text-slate-500">{detail}</p>
        )}
        <ol className="mt-4 grid gap-2 text-xs text-slate-500 sm:grid-cols-2">
          {PHASES.map((step, index) => {
            const state = index < currentStepIndex ? 'Done' : index === currentStepIndex ? 'Now' : 'Next';
            const stateClass = index < currentStepIndex
              ? 'bg-emerald-50 text-emerald-800 ring-emerald-200'
              : index === currentStepIndex
                ? 'bg-slate-900 text-white ring-slate-900'
                : 'bg-slate-50 text-slate-500 ring-slate-200';
            return (
              <li key={step.key} className="flex items-center gap-2">
                <span className={`w-12 shrink-0 rounded-full px-2 py-0.5 text-center text-[11px] font-medium ring-1 ring-inset ${stateClass}`}>
                  {state}
                </span>
                <span>{step.label}</span>
              </li>
            );
          })}
        </ol>
      </div>
      <p className="text-sm text-slate-500">Please leave this tab in the foreground until the test completes.</p>
    </div>
  );
}
